import useShippersAndCarriers from '@/hooks/useShippersAndCarriers';
import { OnwardSwitch, PrimaryButton, Row, SecondaryButton, SectionSubtitle } from '@/styles/blocks';
import { Body2, OnwardToggle, RadioLabelInfo, TabRow, TabSection, TabTitle, TextField } from '../../../../blocks';
import { useClientUser } from '@/hooks';
import { css } from '@emotion/react';
import { Grid, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import React, { useContext, useMemo, useState, useEffect, memo } from 'react';
import { ModalContext } from '../../context';
import { COMPLETE_STATUSES } from '../../../../constants/statusOptions';
import { ClientSelect, DateInput, InfoInput, StatusSelect, TimeSelect, SelectCarrierTable } from '../../../InputFields';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { useSwappedAttributes } from '@/components/ShipmentForm/hooks';
import { post } from '@/utilities/onwardClient';
import { NOTIFY_CARRIERS_OF_LTL_ORDER, UNASSIGN_CARRIER } from '@/constants/apiRoutes';
import { unassignCarrierMessage } from '@/utilities/unassignCarrierMessage';
import { withStyles } from '@material-ui/core/styles';
import useTrackedComms from '@/hooks/useTrackedComms';
import Snackbar from '@/components/Snackbar';
import { SOURCE_MAPPINGS } from '../../../../constants/admin';
import { AIRPORT_CODES_BY_ZIP } from '@/constants/airportCodes';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import AdminNotes from './AdminNotes';
import RatesSection from '../RatesSection';
import { useQuery } from '@apollo/client';
import { QUERY_ONWARD_ADMINS } from '@/graphql/queries/users';
import { MenuItem } from '@material-ui/core';
import { toMinuteIso } from '@/utilities/toMinuteIso';

const CustomTooltip = withStyles(() => ({
    tooltip: {
        fontSize: 14,
    },
}))(Tooltip);

const numberValOrDefault = (numberVal, defaultVal) => {
    if (numberVal || numberVal === 0) {
        return numberVal;
    }
    return defaultVal;
};

const black = css`
    color: black;
`;

/**
 * Admin section of the Edit Order modal: shipper and carrier assignment (including middle mile
 * and unassign), marketplace toggle, rates, and admin date and time overrides.
 * Takes no props; reads `ModalContext`.
 */
const AdminTab = memo(() => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { order, adminHasError, options } = modalState;
    const [lockedClients, setLockedClients] = useState([]);
    const [saving, setSaving] = useState(false);
    const [notifyCarriersMessage, setNotifyCarriersMessage] = useState('');
    const [selectedCarriers, setSelectedCarriers] = useState([]);
    const [selectedMmCarriers, setSelectedMmCarriers] = useState([]);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [unassignLoading, setUnassignLoading] = useState(false);

    // Split per leg. Each "Assign" button is blocked by its own leg's integration data -- a live
    // line haul must not block a first-mile assignment, or vice versa -- and each section now
    // carries its own Unassign, because the endpoint takes the leg to tear down and acts on that
    // leg alone.
    //
    // The middle mile half is what closes the gap: with only the dispatch leg gated, an order
    // dispatched on the line haul alone showed no Unassign button anywhere and its middle mile
    // carrier could be reassigned freely, leaving the old carrier's match live and the order mapped
    // to two lane routes.
    const hasDispatchIntegration = !!order.dispatch_integration_type;
    const hasMiddleMileIntegration = !!order.middle_mile_integration_type;

    // Which leg the confirm dialog will act on, so one dialog serves both buttons. Null means
    // closed -- the dialog's open state reads this rather than a separate flag, so the two can
    // never disagree about which leg is about to be torn down.
    const [unassignLeg, setUnassignLeg] = useState(null);

    // The unassign is the user's own write to the order. Run it whole (request and read-back)
    // through the modal's `runOwnWrite`, so the live "This order was updated" prompt doesn't fire on
    // its echo. Falls back to a plain call outside the modal.
    const runOwnWrite = callbacks.runOwnWrite || ((write) => write());

    const handleUnassignCarrier = () =>
        runOwnWrite(async () => {
            if (!unassignLeg) return;

            const leg = unassignLeg;
            setUnassignLoading(true);
            try {
                const resp = await post(UNASSIGN_CARRIER, { order_id: order.order_id, leg });
                const { severity, message } = unassignCarrierMessage(resp);
                // Two snackbars, hardcoded success and error, so the helper's `warning` renders red
                // here. Same as it did before the helper existed -- this tab has no amber to route to.
                if (severity === 'success') {
                    setSuccessMsg(message);
                } else {
                    setErrorMsg(message);
                }

                // Its own try/catch. The unassign has already landed by this point and the resync is a
                // separate round trip -- the caller's `refetchOrder` is an Apollo refetch that rejects
                // on a network error -- so folding it into the block above relabelled a completed
                // unassign as a failure and told the operator to retry a teardown that had succeeded.
                //
                // Awaited: clearing unassignLoading below re-enables the button and re-renders this tab
                // off the clone, so returning before the resync lands leaves the old carrier on screen
                // with Unassign live again.
                try {
                    await callbacks.refetchOrder?.(leg);
                } catch (refetchError) {
                    console.error(refetchError);
                    // Success is cleared rather than left alongside: both snackbars anchor top right,
                    // and this is the case that actually needs the operator's attention. A clone still
                    // holding the cleared carrier is what Done writes back, which is the whole reason
                    // the resync exists.
                    setSuccessMsg('');
                    setErrorMsg(
                        'Carrier unassigned, but the order could not be refreshed. Reload the page before making further changes.'
                    );
                }
            } catch (e) {
                setErrorMsg('Failed to unassign carrier. Please try again.');
                console.error(e);
            } finally {
                setUnassignLoading(false);
                setUnassignLeg(null);
            }
        });

    const { isOnwardAdmin } = useClientUser();
    const { zip } = useSwappedAttributes(order);

    const shipperIds = useMemo(() => {
        if (!options.adminTabShipperIds) return null;
        const ids = [...options.adminTabShipperIds];
        if (order.shipper_id && !ids.includes(order.shipper_id)) ids.push(order.shipper_id);
        return ids;
    }, [options.adminTabShipperIds, order.shipper_id]);

    const carrierIds = useMemo(() => {
        if (!options.adminTabCarrierIds) return null;
        const ids = [...options.adminTabCarrierIds];
        [order.carrier_id, order.middle_mile_carrier_id, order.warehouse_carrier_id].filter(Boolean).forEach((id) => {
            if (!ids.includes(id)) ids.push(id);
        });
        return ids;
    }, [options.adminTabCarrierIds, order.carrier_id, order.middle_mile_carrier_id, order.warehouse_carrier_id]);

    const [shippers, _carriers, _mmCarriers] = useShippersAndCarriers(
        options.enableAdminTestAccounts,
        shipperIds,
        carrierIds
    );

    const { data: onwardAdminsData } = useQuery(QUERY_ONWARD_ADMINS);
    const onwardAdmins = useMemo(() => onwardAdminsData?.users || [], [onwardAdminsData]);

    const orderAirportCodes = useMemo(() => {
        return {
            originCode: AIRPORT_CODES_BY_ZIP[order?.pickup_zip] || null,
            destinationCode: AIRPORT_CODES_BY_ZIP[order?.dropoff_zip] || null,
        };
    }, [order]);

    const carriers = useMemo(() => {
        const carriersWithAirportCodes = _carriers.map((carrier) => {
            const airportCodes = [];
            carrier.locations?.forEach((location) => {
                const airportCode = AIRPORT_CODES_BY_ZIP[location.business_zip];
                if (airportCode && !airportCodes.includes(airportCode)) {
                    if (airportCode === orderAirportCodes.destinationCode) {
                        airportCodes.unshift(airportCode);
                    } else {
                        airportCodes.push(airportCode);
                    }
                }
            });
            return {
                ...carrier,
                airportCodes,
            };
        });

        return carriersWithAirportCodes
            .filter((carrier) => {
                const searchLower = searchValue.toLowerCase();
                return (
                    carrier?.business_name?.toLowerCase().includes(searchLower) ||
                    carrier?.email?.toLowerCase().includes(searchLower) ||
                    carrier?.airportCodes?.some((code) => code?.toLowerCase().includes(searchLower))
                );
            })
            .sort((a, b) => {
                const aHasDestCode = a.airportCodes.includes(orderAirportCodes.destinationCode);
                const bHasDestCode = b.airportCodes.includes(orderAirportCodes.destinationCode);
                if (aHasDestCode && !bHasDestCode) return -1;
                if (!aHasDestCode && bHasDestCode) return 1;
                return 0;
            });
    }, [_carriers, orderAirportCodes.destinationCode, searchValue]);

    const showMMCarrierSelect = useMemo(() => {
        return (
            order?.has_middle_mile_origin &&
            order?.shipper_rate_override?.override_type !== 'E2E' &&
            !order?.middle_mile_shipper_rate_override?.assign_id
        );
    }, [order]);

    const mmCarriers = useMemo(() => {
        const mmCarriersWithAirportCodes = _mmCarriers.map((carrier) => {
            const airportCodes = [];
            carrier.locations?.forEach((location) => {
                const airportCode = AIRPORT_CODES_BY_ZIP[location.business_zip];
                if (airportCode && !airportCodes.includes(airportCode)) {
                    if (airportCode === orderAirportCodes.destinationCode) {
                        airportCodes.unshift(airportCode);
                    } else {
                        airportCodes.push(airportCode);
                    }
                }
            });
            return {
                ...carrier,
                airportCodes,
            };
        });

        return mmCarriersWithAirportCodes
            .filter((carrier) => {
                const searchLower = searchValue.toLowerCase();
                return (
                    carrier?.business_name?.toLowerCase().includes(searchLower) ||
                    carrier?.email?.toLowerCase().includes(searchLower) ||
                    carrier?.airportCodes?.some((code) => code?.toLowerCase().includes(searchLower))
                );
            })
            .sort((a, b) => {
                const aHasDestCode = a.airportCodes.includes(orderAirportCodes.destinationCode);
                const bHasDestCode = b.airportCodes.includes(orderAirportCodes.destinationCode);
                if (aHasDestCode && !bHasDestCode) return -1;
                if (!aHasDestCode && bHasDestCode) return 1;
                return 0;
            });
    }, [_mmCarriers, orderAirportCodes.destinationCode, searchValue]);

    const orderTZ = useMemo(() => {
        return order[zip]
            ? zipcode_to_timezone.lookup(order[zip]) ||
                  (order[zip].startsWith('9') ? 'America/Los_Angeles' : 'America/New_York')
            : 'America/New_York';
    }, [order, zip]);

    const handleNotifyCarriers = async () => {
        setSaving(true);
        setNotifyCarriersMessage('');
        try {
            const carriersToNotify = selectedCarriers.filter(
                (carrierId) => !lockedClients.some((locked) => locked.client_id === carrierId)
            );

            const response = await post(NOTIFY_CARRIERS_OF_LTL_ORDER, {
                carriers: carriersToNotify.map((carrierId) => {
                    const carrier = carriers.find((c) => c.client_id === carrierId);
                    return {
                        business_name: carrier.business_name,
                        email: carrier.email,
                        contract_rates: !!carrier?.user?.circles?.['hide-market-rates'],
                        client_id: carrier.client_id,
                    };
                }),
                order,
            });

            setLockedClients([...lockedClients, ...(response?.data?.successes || [])]);

            if (response?.data?.failures.length) {
                setNotifyCarriersMessage(
                    `Partial success: There was an error attempting to notify the following carriers: ${response?.data?.failures
                        .map((carrier) => carrier.business_name)
                        .join(', ')}`
                );
            } else {
                setNotifyCarriersMessage('All carriers were successfully notified.');
            }
        } catch (e) {
            setNotifyCarriersMessage('There was an error attempting to notify carriers.');
            console.error(e);
        } finally {
            setSelectedCarriers([]);
            setSaving(false);
        }
    };

    const isMarketplaceOrder = useMemo(() => {
        if (!order) return false;
        return (
            ['pending', 'open'].includes(order?.order_status) &&
            !order?.oms &&
            !order.routes?.length &&
            !order.carrier_id
        );
    }, [order]);

    const { loading: trackedCommsLoading, tracked_communications } = useTrackedComms(
        order.order_id,
        !isMarketplaceOrder
    );

    const disableNotifyCarriersButton = useMemo(() => {
        if (saving) return { disabled: true, reason: 'Notifying carriers already currently in progress.' };
        if (order.order_status !== 'open')
            return { disabled: true, reason: `Cannot notify carriers unless order is in 'Open' order status` };
        if (!selectedCarriers.length)
            return { disabled: true, reason: 'No new carriers have been selected for notifying.' };
        if (!isMarketplaceOrder) return { disabled: true, reason: 'Not a marketplace order.' };
        if (trackedCommsLoading) return { disabled: true, reason: 'Loading...' };
        return false;
    }, [saving, order, selectedCarriers, isMarketplaceOrder]);

    const pricingOverride = useMemo(() => {
        const override = order.oms ? order.onward_rate_override : order.shipper_rate_override;

        return override || {};
    }, [order]);

    const syncDateAndTimeframes = (newDate, oldWindowStart, oldWindowEnd) => {
        const newDateObj = new Date(newDate);
        const newWindowStart = new Date(oldWindowStart);
        newWindowStart.setDate(newDateObj.getUTCDate());
        newWindowStart.setMonth(newDateObj.getUTCMonth());
        newWindowStart.setFullYear(newDateObj.getFullYear());

        const newWindowEnd = new Date(oldWindowEnd);
        newWindowEnd.setDate(newDateObj.getUTCDate());
        newWindowEnd.setMonth(newDateObj.getUTCMonth());
        newWindowEnd.setFullYear(newDateObj.getUTCFullYear());

        return { newStartIso: newWindowStart.toISOString(), newEndIso: newWindowEnd.toISOString() };
    };
    let cantCopy = order.order_status !== 'open' || !!order?.carrier_id || !!order?.routes?.length || !!order?.oms;

    return (
        <>
            <TabTitle
                collapsed={collapsed}
                callbacks={{ setCollapsed }}
                css={css`
                    align-items: center;
                    justify-content: space-between;
                    ${collapsed ? '' : 'margin-bottom: 20px;'}
                `}
            >
                Admin
            </TabTitle>

            {collapsed ? null : (
                <>
                    <TabRow>
                        <Grid item xs={6}>
                            <StatusSelect
                                routed={Boolean(order.routes?.length)}
                                value={order.order_status}
                                disabled={['on_hold_awaiting_payment'].includes(order.order_status)}
                                onChange={(status) =>
                                    callbacks.modifyOrder({
                                        order_status: status,
                                        ...(COMPLETE_STATUSES.includes(status) &&
                                        !COMPLETE_STATUSES.includes(order.order_status)
                                            ? {
                                                  completion_time: new Date().toISOString(),
                                                  completion_source: COMPLETION_SOURCES.WEB,
                                              }
                                            : {}),
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <OnwardToggle
                                css={css`
                                    margin: 0;
                                `}
                                value={!order.oms}
                                onChange={() => callbacks.modifyOrder({ oms: !order.oms })}
                                disabled={order.routes?.length}
                            />
                            <RadioLabelInfo
                                css={css`
                                    color: #000;
                                    padding-top: 3px;
                                    margin-left: 3px;
                                `}
                            >
                                Marketplace Order
                            </RadioLabelInfo>
                        </Grid>
                    </TabRow>

                    <TabRow>
                        <Grid item xs={6}>
                            <ClientSelect
                                value={order.shipper_id}
                                options={shippers}
                                onChange={(shipperId) => callbacks.modifyOrder({ shipper_id: shipperId })}
                                allowEmpty={false}
                                type="shipper"
                                css={css`
                                    margin-right: 0.5rem;
                                `}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={'Carrier'}
                                value={
                                    carriers?.find((carrier) => carrier.client_id === order?.carrier_id)
                                        ?.business_name || 'None'
                                }
                                disabled
                            />
                        </Grid>
                    </TabRow>

                    <TabRow>
                        <Grid item xs={6}>
                            <TextField
                                select
                                variant="outlined"
                                fullWidth
                                label="Assigned Admin"
                                value={order.assigned_admin_id || ''}
                                onChange={(e) =>
                                    callbacks.modifyOrder({
                                        assigned_admin_id: e.target.value || null,
                                    })
                                }
                            >
                                <MenuItem value="">--</MenuItem>
                                {onwardAdmins.map((u) => (
                                    <MenuItem key={u.user_id} value={u.user_id}>
                                        {u.username || u.email}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} />
                    </TabRow>
                    <TabRow>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                label={'Origin Airport Code'}
                                value={orderAirportCodes.originCode || 'N/A'}
                                disabled
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                label={'Destination Airport Code'}
                                value={orderAirportCodes.destinationCode || 'N/A'}
                                disabled
                                fullWidth
                            />
                        </Grid>
                    </TabRow>

                    <TabRow>
                        <Grid item xs={6} style={{ margin: '0 auto' }}>
                            <TextField
                                variant="outlined"
                                label={'Search Carriers'}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    </TabRow>

                    <TabSection
                        css={css`
                            display: flex;
                            align-items: center;
                            gap: 6px;
                        `}
                    >
                        Assign Final Mile Carrier
                        {hasDispatchIntegration && (
                            <CustomTooltip title="This order has active integration data. Use 'Unassign Carrier' below before reassigning.">
                                <InfoOutlinedIcon
                                    css={css`
                                        font-size: 18px;
                                        color: #f59e0b;
                                        cursor: help;
                                    `}
                                />
                            </CustomTooltip>
                        )}
                    </TabSection>
                    <SelectCarrierTable
                        carriers={carriers}
                        selectedCarriers={selectedCarriers}
                        setSelectedCarriers={setSelectedCarriers}
                        order={order}
                        disabled={hasDispatchIntegration}
                    />

                    <TabRow>
                        <Grid item xs={6}>
                            <CustomTooltip title="Email will be sent to new carrier once you click Done.">
                                <span>
                                    <OnwardToggle
                                        css={css`
                                            margin: 0;
                                        `}
                                        value={modalState.sendCarrierAssignedEmail}
                                        disabled={selectedCarriers.length !== 1 || hasDispatchIntegration}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                modalState.setSendCarrierAssignedEmail(true);
                                            } else {
                                                modalState.setSendCarrierAssignedEmail(false);
                                            }
                                        }}
                                    />
                                    <RadioLabelInfo
                                        css={css`
                                            color: #000;
                                            padding-top: 3px;
                                            margin-left: 3px;
                                        `}
                                    >
                                        Email/Notify Carrier of Assignment
                                    </RadioLabelInfo>
                                </span>
                            </CustomTooltip>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            css={css`
                                display: flex;
                                justify-content: space-between;
                            `}
                        >
                            <CustomTooltip
                                title={
                                    hasDispatchIntegration
                                        ? 'Unassign the current carrier before reassigning.'
                                        : 'Changes not final until you click "Done".'
                                }
                            >
                                <span>
                                    <PrimaryButton
                                        disabled={
                                            selectedCarriers.length !== 1 ||
                                            !!order.oms ||
                                            !!pricingOverride.assign ||
                                            hasDispatchIntegration
                                        }
                                        onClick={() =>
                                            callbacks.modifyOrder({
                                                carrier_id: selectedCarriers[0],
                                                is_middle_mile: false,
                                                ...(order.order_status === 'open' ? { order_status: 'claimed' } : {}),
                                                last_modified_source: 'ADMIN',
                                            })
                                        }
                                    >
                                        Assign Selected Carrier
                                    </PrimaryButton>
                                </span>
                            </CustomTooltip>
                            {hasDispatchIntegration && (
                                <SecondaryButton disabled={unassignLoading} onClick={() => setUnassignLeg('DISPATCH')}>
                                    Unassign Carrier
                                </SecondaryButton>
                            )}
                            {disableNotifyCarriersButton ? (
                                <CustomTooltip title={disableNotifyCarriersButton.reason}>
                                    <span>
                                        <PrimaryButton onClick={handleNotifyCarriers} disabled={true}>
                                            Notify Carriers
                                        </PrimaryButton>
                                    </span>
                                </CustomTooltip>
                            ) : (
                                <PrimaryButton onClick={handleNotifyCarriers}>Notify Carriers</PrimaryButton>
                            )}
                        </Grid>
                    </TabRow>

                    {notifyCarriersMessage && (
                        <TabRow>
                            <Grid item>
                                <span>{notifyCarriersMessage}</span>
                            </Grid>
                        </TabRow>
                    )}

                    {showMMCarrierSelect && (
                        <>
                            <TabSection>Assign Middle Mile Carrier</TabSection>
                            <SelectCarrierTable
                                carriers={mmCarriers}
                                selectedCarriers={selectedMmCarriers}
                                setSelectedCarriers={setSelectedMmCarriers}
                                order={order}
                                disabled={hasMiddleMileIntegration}
                            />

                            <CustomTooltip
                                title={
                                    hasMiddleMileIntegration
                                        ? 'Unassign the current carrier before reassigning.'
                                        : 'Changes not final until you click "Done".'
                                }
                            >
                                <span>
                                    <PrimaryButton
                                        // Mirrors the first-mile button: reassigning over a live
                                        // line haul leaves the old carrier's match open and the
                                        // order mapped to a second lane route.
                                        disabled={selectedMmCarriers.length !== 1 || hasMiddleMileIntegration}
                                        onClick={() =>
                                            callbacks.modifyOrder({
                                                middle_mile_carrier_id: selectedMmCarriers[0],
                                                last_modified_source: 'ADMIN',
                                            })
                                        }
                                    >
                                        Assign Middle Mile Carrier
                                    </PrimaryButton>
                                </span>
                            </CustomTooltip>
                        </>
                    )}

                    {/* Its own button, posting its own leg. One shared Unassign used to cancel the
                        line haul at the vendor whenever an admin unassigned the final mile carrier,
                        dropping a booking nobody asked to cancel.

                        Outside `showMMCarrierSelect` on purpose. That gate is about whether a middle
                        mile carrier can be *assigned* here, and it excludes E2E orders and any order
                        already routed by an assign_id -- both of which can still be carrying a live
                        line haul. Inside it, those orders would have a middle mile booking and no
                        way to unassign it, which the single shared button used to cover. */}
                    {hasMiddleMileIntegration && (
                        <SecondaryButton disabled={unassignLoading} onClick={() => setUnassignLeg('MIDDLE_MILE')}>
                            Unassign Carrier
                        </SecondaryButton>
                    )}

                    {(isOnwardAdmin || order?.shipper_rate_override?.marketplace_tier !== 'T3_FULLY_MANAGED') && (
                        <>
                            <TabSection className="mt-3">Admin - Rates</TabSection>
                            <RatesSection />
                            <hr
                                css={css`
                                    margin: 0;
                                    border: none;
                                    border-top: 2px solid gray;
                                `}
                            />
                        </>
                    )}

                    {order.source_form === 'CUSTOMER' && (
                        <TabRow>
                            <Grid item xs={6}>
                                <InfoInput
                                    value={order.customer_rate ? order.customer_rate.toFixed(2) : 'N/A'}
                                    label="Customer Authorized Amount"
                                />
                            </Grid>
                        </TabRow>
                    )}

                    <div className="mt-3">
                        <TabRow>
                            <Grid item xs={6}>
                                <DateInput
                                    value={order.pickup_date}
                                    onChange={(newPickupDate) => {
                                        if (!newPickupDate) {
                                            callbacks.modifyOrder({
                                                pickup_date: null,
                                                pickup_window_start: null,
                                                pickup_window_end: null,
                                            });
                                        } else if (order.pickup_window_start && order.pickup_window_end) {
                                            const { newStartIso, newEndIso } = syncDateAndTimeframes(
                                                newPickupDate,
                                                order.pickup_window_start,
                                                order.pickup_window_end
                                            );

                                            callbacks.modifyOrder({
                                                pickup_date: newPickupDate,
                                                pickup_window_start: newStartIso,
                                                pickup_window_end: newEndIso,
                                            });
                                        } else {
                                            callbacks.modifyOrder({
                                                pickup_date: newPickupDate,
                                            });
                                        }
                                    }}
                                    label="Pickup Date"
                                    tz={orderTZ}
                                    firstAvailable={order.first_available_date}
                                    disabled={order.routes?.length}
                                    error={adminHasError.pickup_date}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DateInput
                                    value={order.delivery_date}
                                    onChange={(newDeliveryDate) => {
                                        if (!newDeliveryDate) {
                                            callbacks.modifyOrder({
                                                delivery_date: null,
                                                del_window_start: null,
                                                del_window_end: null,
                                            });
                                        } else if (order.del_window_start && order.del_window_end) {
                                            const { newStartIso, newEndIso } = syncDateAndTimeframes(
                                                newDeliveryDate,
                                                order.del_window_start,
                                                order.del_window_end
                                            );

                                            callbacks.modifyOrder({
                                                delivery_date: newDeliveryDate,
                                                del_window_start: toMinuteIso(newStartIso),
                                                del_window_end: toMinuteIso(newEndIso),
                                            });
                                        } else {
                                            callbacks.modifyOrder({
                                                delivery_date: newDeliveryDate,
                                            });
                                        }
                                    }}
                                    label="Delivery Date"
                                    tz={orderTZ}
                                    firstAvailable={order.first_available_date}
                                    disabled={order.routes?.length}
                                    error={adminHasError.delivery_date}
                                />
                            </Grid>
                        </TabRow>

                        <TabRow>
                            <Grid item xs={3}>
                                <TimeSelect
                                    value={order.pickup_window_start}
                                    label="Pickup Start"
                                    tz={orderTZ}
                                    date={order.pickup_date}
                                    onChange={(val) => callbacks.modifyOrder({ pickup_window_start: val })}
                                    disabled={order.routes?.length || !order.pickup_date}
                                    error={adminHasError.pickup_window}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TimeSelect
                                    value={order.pickup_window_end}
                                    label="Pickup End"
                                    tz={orderTZ}
                                    date={order.pickup_date}
                                    onChange={(val) => callbacks.modifyOrder({ pickup_window_end: val })}
                                    disabled={order.routes?.length || !order.pickup_date}
                                    error={adminHasError.pickup_window}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TimeSelect
                                    value={order.del_window_start}
                                    label="Delivery Start"
                                    tz={orderTZ}
                                    date={order.delivery_date}
                                    onChange={(val) => callbacks.modifyOrder({ del_window_start: toMinuteIso(val) })}
                                    disabled={order.routes?.length || !order.delivery_date}
                                    error={adminHasError.delivery_window}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TimeSelect
                                    value={order.del_window_end}
                                    label="Delivery End"
                                    tz={orderTZ}
                                    date={order.delivery_date}
                                    onChange={(val) => callbacks.modifyOrder({ del_window_end: toMinuteIso(val) })}
                                    disabled={order.routes?.length || !order.delivery_date}
                                    error={adminHasError.delivery_window}
                                />
                            </Grid>
                        </TabRow>
                    </div>

                    <TabRow>
                        <Grid item>
                            <Body2 css={black}>Source: {SOURCE_MAPPINGS[order.source_form] || 'Unknown'}</Body2>
                        </Grid>
                    </TabRow>
                    {order.source_form === 'CUSTOMER' && order.payment_id ? (
                        <TabRow>
                            <Grid item>
                                <Body2 css={black}>Stripe PI: {order.payment_id}</Body2>
                            </Grid>
                        </TabRow>
                    ) : null}
                </>
            )}

            <Dialog open={!!unassignLeg} onClose={() => setUnassignLeg(null)}>
                <DialogTitle>Unassign Carrier?</DialogTitle>
                <DialogContent>
                    {/* Names the leg, because the other one is deliberately left running and an
                        operator needs to know that before confirming. */}
                    <DialogContentText>
                        {unassignLeg === 'MIDDLE_MILE'
                            ? 'This will attempt to cancel the line haul booking with the middle mile partner, then clear the middle mile carrier and integration data. The final mile carrier is left as it is. This cannot be undone. Proceed?'
                            : 'This will attempt to cancel the order with the final mile integration partner, then clear the final mile carrier and integration data. The middle mile carrier is left as it is. This cannot be undone. Proceed?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={() => setUnassignLeg(null)} disabled={unassignLoading}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleUnassignCarrier} disabled={unassignLoading}>
                        {unassignLoading ? 'Unassigning...' : 'Yes, Unassign'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                severity="success"
                open={!!successMsg}
                message={successMsg}
                autohide={2000}
                handleClose={() => setSuccessMsg('')}
                disableClose={true}
            />
            <Snackbar
                severity="error"
                open={!!errorMsg}
                message={errorMsg}
                autohide={2000}
                handleClose={() => setErrorMsg('')}
                disableClose={true}
            />
        </>
    );
});

export default AdminTab;
